import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import CompositionEditor from "@/components/CompositionEditor";
import OrderHint from "@/components/OrderHint";
import { uploadMedia } from "@/services/media";
import {
  cancelRecognition, createRecognition, fetchRecognition, runRecognition,
  type Recognition, type RecognitionCandidate,
} from "@/services/stock";

export type Picked = { productId: number; name: string | null; sku: string | null; remaining: number };

// His `realSku`: a supplier's reference has a dash and is not one of Myotaku's generic "UNKN-n".
const supplierRef = (sku: string | null | undefined) => (sku && sku.includes("-") && !/^UNKN-\d+$/.test(sku) ? sku : "");

// His `refs` list on the result card: Myotaku ref, supplier ref (or "generic item"), supplier,
// type · room, size.
function CandidateFacts({ c }: { c: RecognitionCandidate }) {
  const { t } = useT();
  const sup = supplierRef(c.sku);
  const rows: [string, React.ReactNode][] = [
    [t("ident.refMyo"), <span className="font-mono">{c.product_id}</span>],
    [t("ident.refSup"), sup ? <span className="font-mono">{sup}</span> : <span className="text-slate-500">{t("ident.generic")}</span>],
    ...(c.supplier ? [[t("ident.supplier"), c.supplier] as [string, React.ReactNode]] : []),
    [t("ident.type"), <>{c.type_name || c.type || "—"}{c.rooms?.length ? <span className="text-slate-500"> · {c.rooms.join(" · ")}</span> : null}</>],
    ...(c.size ? [[t("ident.size"), c.size] as [string, React.ReactNode]] : []),
  ];
  return (
    <dl className="mt-1 grid grid-cols-[8rem_1fr] gap-x-2 gap-y-0.5 text-xs">
      {rows.map(([k, v]) => <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className="text-slate-800">{v}</dd></div>)}
    </dl>
  );
}

// His `confClass` / `confLabel`: 75 and up is sure, 45 and up likely, below that to be checked.
const level = (c: number) => (c >= 75 ? "sure" : c >= 45 ? "likely" : "check");
const tone = { sure: "bg-emerald-100 text-emerald-800", likely: "bg-amber-100 text-amber-800", check: "bg-red-100 text-red-800" };

// His step 2, "Photo pour reconnaissance": one photo of the whole piece; the worker looks for it in the
// return's furniture, then in the catalogue if that is not sure. The model never forces a pick — the
// person confirms with "C'est ce meuble" or picks by hand below.
export default function PhotoRecognition({ returnEventId, warehouseId = null, picked, pickedByHand, onStarted, onPick, onUnpick }: {
  returnEventId: number | null;
  warehouseId?: number | null;
  // A piece is already chosen: a change of return then keeps it and asks nothing.
  picked: boolean;
  // Chosen by hand in the return's list (his `_src === "grid"`): the photo is for the record only —
  // kept with the piece, never sent for recognition.
  pickedByHand: boolean;
  // A new photo after a piece chosen from a recognition: that choice goes, the photo is read again.
  onUnpick: () => void;
  onStarted: (recognitionId: number) => void;
  onPick: (p: Picked, confidence: number | null) => void;
}) {
  const { t, locale } = useT();
  const input = useRef<HTMLInputElement>(null);
  const [id, setId] = useState<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const reading = useRef(false);

  const start = useMutation({
    mutationFn: async ({ file, read }: { file: File; read: boolean }) => {
      const r = await createRecognition({ return_event_id: returnEventId, locale });
      onStarted(r.id);
      setId(r.id);
      await uploadMedia("recognition", r.id, file);
      return read ? runRecognition(r.id) : r;
    },
    onSuccess: (r) => show(r),
  });
  // A new run's state goes straight onto the screen, so the watching starts again (a finished
  // recognition is no longer polled).
  const qc = useQueryClient();
  const show = (r: Recognition) => qc.setQueryData(["recognition", r.id], r);
  const again = useMutation({
    mutationFn: (o: { scope?: "catalogue"; returnEventId?: number | null } = {}) => runRecognition(id as number, o.scope, o.returnEventId),
    onSuccess: show,
  });
  // His "Annuler": the run stops before its next paid call; the manual choice stays as it was.
  const stop = useMutation({ mutationFn: () => cancelRecognition(id as number), onSuccess: show });
  const q = useQuery({
    queryKey: ["recognition", id], queryFn: () => fetchRecognition(id as number), enabled: id != null,
    // Watched while it is queued or running, and while a photo sent for reading is still "new"; a
    // photo kept only for the record is not watched.
    refetchInterval: (s) => {
      const st = s.state.data?.status ?? "";
      return ["pending", "running"].includes(st) || (st === "new" && reading.current) ? 2500 : false;
    },
  });
  const rec = q.data;
  const busy = start.isPending || again.isPending || (rec != null && ["pending", "running"].includes(rec.status));
  const r = rec?.status === "done" ? rec.result : null;

  // His pickRetour: another return chosen after the photo, nothing picked yet — the same photo is
  // compared again with the new return's items.
  const lastReturn = useRef(returnEventId);
  useEffect(() => {
    if (lastReturn.current === returnEventId) return;
    lastReturn.current = returnEventId;
    if (id != null && !picked && rec && rec.status !== "new") {
      if (busy) stop.mutate();
      again.mutate({ returnEventId });
    }
  }, [returnEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const take = (file: File | undefined) => {
    if (!file) return;
    if (busy && id != null) stop.mutate(); // a new photo replaces the run in progress
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    if (picked && !pickedByHand) onUnpick();
    reading.current = !pickedByHand;
    start.mutate({ file, read: !pickedByHand });
  };
  const pick = (c: RecognitionCandidate, confidence: number | null) =>
    onPick({ productId: c.product_id, name: c.name, sku: c.sku, remaining: Math.max(1, c.remaining ?? 1) }, confidence);

  return (
    <div className="mb-4 rounded-lg bg-slate-50 p-3">
      <p className="text-sm font-medium text-slate-700">{t("ident.title")}</p>
      <p className="mb-2 text-xs text-slate-500">{pickedByHand ? t("ident.hintGrid") : t("ident.hint")}</p>
      <div className="flex flex-wrap items-start gap-3">
        {preview && <img src={preview} alt="" className="h-28 w-28 rounded-lg object-cover ring-1 ring-slate-200" />}
        <div className="space-y-2">
          <button onClick={() => input.current?.click()} disabled={start.isPending}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
            {preview ? t("ident.retake") : t("ident.take")}
          </button>
          <input ref={input} type="file" accept="image/*" capture="environment" hidden
            onChange={(e) => { take(e.target.files?.[0]); e.target.value = ""; }} />
          {start.isPending && !id && <p className="text-xs text-slate-500">{t("ident.sending")}</p>}
          {busy && id != null && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              ⏳ {rec?.phase ? t(`ident.phase.${rec.phase}`) : t("ident.analyzing")}
              <button onClick={() => stop.mutate()} disabled={stop.isPending} className="rounded px-2 py-0.5 ring-1 ring-slate-300">{t("ident.cancel")}</button>
            </p>
          )}
          {(start.error || rec?.status === "failed") && (
            <div className="space-y-1">
              <p className="text-xs text-red-700">
                {rec?.error_kind === "unavailable" ? t("ident.noAi") : rec?.error_kind === "rate_limited" ? t("ident.aiLimit") : t("ident.failed")}
              </p>
              {rec?.status === "failed" && rec.error_kind !== "unavailable" && (
                <button onClick={() => again.mutate({})} disabled={again.isPending}
                  className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("ident.recognize")}</button>
              )}
            </div>
          )}
          {rec?.status === "cancelled" && !busy && (
            <button onClick={() => again.mutate({})} disabled={again.isPending}
              className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("ident.recognize")}</button>
          )}
        </div>
      </div>

      {r && (
        <div className="mt-3 space-y-2">
          {r.top && r.retour_checked && r.source === "catalogue" && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">{t("ident.notInRetour")}</p>
          )}
          {r.top ? (
            <div className="rounded-lg bg-white p-3 ring-1 ring-emerald-300">
              {/* His `cmp`: your photo beside the catalogue photo. */}
              <div className="flex gap-3">
                {preview && (
                  <figure className="text-center text-[11px] text-slate-500">
                    <img src={preview} alt="" className="h-28 w-28 rounded object-cover ring-1 ring-slate-200" />
                    <figcaption>{t("ident.yourPhoto")}</figcaption>
                  </figure>
                )}
                <figure className="text-center text-[11px] text-slate-500">
                  {r.top.image_url ? <img src={r.top.image_url} alt="" className="h-28 w-28 rounded object-contain ring-1 ring-slate-200" />
                    : <div className="h-28 w-28 rounded bg-slate-100" />}
                  <figcaption>{t("ident.catPhoto")}</figcaption>
                </figure>
              </div>
              <div className="mt-2 text-sm">
                <p className="font-medium text-slate-900">
                  {r.top.name}
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${tone[level(r.confidence)]}`}>
                    {t(`ident.${level(r.confidence)}`)} · {r.confidence} %
                  </span>
                </p>
                <CandidateFacts c={r.top} />
                <CompositionEditor productId={r.top.product_id} productName={r.top.name} />
                {r.top.description && <p className="mt-1 text-xs text-slate-700">{r.top.description}</p>}
                <OrderHint productId={r.top.product_id} sku={r.top.sku} warehouseId={warehouseId} />
                <p className="mt-1 text-xs text-slate-500">{r.source === "retour" ? t("ident.fromReturn") : t("ident.fromCatalogue")}</p>
                {(r.seen || r.reason) && (
                  <p className="mt-1 text-xs text-slate-600">{r.seen && <>{t("ident.seen")} : {r.seen.replace(/[.\s]+$/, "")}. </>}{r.reason}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button onClick={() => pick(r.top as RecognitionCandidate, r.confidence)}
                    className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white">{t("ident.pickThis")}</button>
                  {r.top.public_url && <a href={r.top.public_url} target="_blank" rel="noreferrer" className="text-xs text-slate-600 underline">{t("stock.openCatalogue")}</a>}
                  {r.top.in_catalogue === false && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{t("ident.notInCat")}</span>}
                </div>
              </div>
            </div>
          ) : (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
              {r.retour_checked && !r.catalogue_offered ? t("ident.noMatch") : t("ident.noMatchAny")}
              {r.seen && <span className="text-slate-500"> ({t("ident.seen")} : {r.seen})</span>}
              {!r.catalogue_offered && <span className="block text-slate-500">{t("stock.searchUnavailable")}</span>}
            </p>
          )}
          {r.alts.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">{t("ident.other")}</p>
              <ul className="space-y-1">
                {r.alts.map((a) => (
                  <li key={a.product_id}>
                    <button onClick={() => pick(a, null)} className="flex w-full items-center gap-3 rounded-lg bg-white p-2 text-left ring-1 ring-slate-200 hover:bg-slate-50">
                      {a.image_url ? <img src={a.image_url} alt="" className="h-10 w-10 rounded object-contain" /> : <span className="h-10 w-10 rounded bg-slate-200" />}
                      <span className="min-w-0 flex-1 text-sm text-slate-900">{a.name}
                        {(a.type_name || a.type) && <span className="text-slate-500"> · {a.type_name || a.type}</span>}
                        {/* His altHtml line: Myotaku ref · supplier ref · room · size. */}
                        <span className="block text-xs text-slate-500">
                          {[String(a.product_id), supplierRef(a.sku), a.rooms?.join(" · "), a.size, a.src === "retour" ? t("ident.fromReturn") : t("ident.fromCatalogue")].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* His "Chercher dans tout le catalogue", after every finished recognition. */}
          <button onClick={() => again.mutate({ scope: "catalogue" })} disabled={busy}
            className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300 disabled:opacity-40">{t("ident.searchCatalogue")}</button>
        </div>
      )}
    </div>
  );
}
